/**
 * Wellbeing Service
 * Handles physical and emotional wellbeing data with FHIR compliance
 */

import { api } from "./api";
import { log } from "@/lib/log";

export interface WellbeingData {
  physicalWellbeing: 1 | 2 | 3 | 4 | 5;
  emotionalWellbeing: 1 | 2 | 3 | 4 | 5;
  recordedAt: string;
}

export interface WellbeingHistoryPoint {
  date: string;
  physicalWellbeing: number;
  emotionalWellbeing: number;
  physicalWellness?: number;
  emotionalWellness?: number;
  timestamp: string;
  formattedDate: string;
}

class WellbeingService {
  private baseUrl = "/wellbeing";

  async saveWellbeingAssessment(
    physicalWellbeing: 1 | 2 | 3 | 4 | 5,
    emotionalWellbeing: 1 | 2 | 3 | 4 | 5,
  ): Promise<void> {
    try {
      log.debug("💾 Saving wellbeing assessment to FHIR database:", {
        physicalWellbeing,
        emotionalWellbeing,
      });

      const response = await api.post(`${this.baseUrl}`, {
        physicalWellbeing,
        emotionalWellbeing,
      });

      log.debug(
        "✅ Wellbeing assessment saved to FHIR database:",
        response.data,
      );

      if (!response.data.success) {
        throw new Error(
          response.data.error?.message || "Failed to save wellbeing assessment",
        );
      }
    } catch (error: any) {
      console.error("❌ Error saving wellbeing assessment:", error);
      throw error;
    }
  }

  async getLatestWellbeing(): Promise<WellbeingData> {
    try {
      log.debug("🔍 Fetching latest wellbeing data from FHIR database");

      const response = await api.get(`${this.baseUrl}/latest`);
      log.debug("✅ Latest wellbeing data from FHIR:", response.data);

      return {
        physicalWellbeing: response.data.data.physicalWellbeing || 3,
        emotionalWellbeing: response.data.data.emotionalWellbeing || 3,
        recordedAt: response.data.data.recordedAt || new Date().toISOString(),
      };
    } catch (error: any) {
      // Gracefully handle 404 - route not implemented yet
      if (error?.response?.status === 404) {
        console.warn("ℹ️ Wellbeing API not yet implemented, using defaults");
      } else {
        console.error("❌ Error fetching latest wellbeing:", error);
      }

      return {
        physicalWellbeing: 3,
        emotionalWellbeing: 3,
        recordedAt: new Date().toISOString(),
      };
    }
  }

  async getWellbeingHistory(): Promise<WellbeingHistoryPoint[]> {
    try {
      log.debug("📊 Fetching wellbeing history from FHIR database (365 days)");

      const response = await api.get(`${this.baseUrl}/history`);
      log.debug("✅ FHIR wellbeing history (365 days):", response.data);

      if (!response.data.data || !response.data.data.history) {
        log.debug("⚠️ No wellbeing history data found");
        return [];
      }

      const wellbeingHistory: WellbeingHistoryPoint[] =
        response.data.data.history.map(
          (item: {
            date: string;
            physicalWellbeing?: number;
            emotionalWellbeing?: number;
            physicalWellness?: number;
            emotionalWellness?: number;
            timestamp?: string;
            formattedDate?: string;
          }) => ({
            date: item.date,
            physicalWellbeing: item.physicalWellbeing || 0,
            emotionalWellbeing: item.emotionalWellbeing || 0,
            physicalWellness:
              item.physicalWellness || item.physicalWellbeing || 0,
            emotionalWellness:
              item.emotionalWellness || item.emotionalWellbeing || 0,
            timestamp: item.timestamp || new Date().toISOString(),
            formattedDate: item.formattedDate || item.date,
          }),
        );

      log.debug(
        `✅ Transformed ${wellbeingHistory.length} FHIR wellbeing data points for charts`,
      );
      log.debug("📊 Sample data point:", wellbeingHistory[0]);
      return wellbeingHistory;
    } catch (error: any) {
      console.error("❌ Error fetching FHIR wellbeing history:", error);
      return [];
    }
  }
}

export const wellbeingService = new WellbeingService();
